import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Checkbox,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@design-system';
import { useSendPuiDialog } from '../hooks/use-send-pui-dialog';
import { PuiLoadingSequence } from './pui-loading-sequence';
import { PuiEmailReview } from './pui-email-review';
import { PuiSendConfirmation } from './pui-send-confirmation';
import { UnderwritingStatus } from '@client/features/underwriting-eligibility/components/underwriting-status';
import { insuranceCarrierDefs } from '@shared/insuranceCarrierDefs';

interface SendPuiDialogProps {
  open: boolean;
  onClose: () => void;
  availablePolicies: PolicyInfo[];
  puiHistory?: PUIHistoryItem[];
}

export function SendPuiDialog({
  open,
  onClose,
  availablePolicies,
  puiHistory = [],
}: SendPuiDialogProps) {
  const {
    step,
    selectedPolicies,
    showLoadingSequence,
    draftData,
    emailResults,
    isPending,
    handleTogglePolicy,
    handleContinue,
    handleBackToSelection,
    handleLoadingComplete,
    handleSend,
  } = useSendPuiDialog({
    open,
    onClose,
    availablePolicies,
  });

  const selectPoliciesContent = () => {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Select Policies</DialogTitle>
          <DialogDescription>
            Choose which policies to include in the anonymous PUI request
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 max-h-[65vh] overflow-y-auto pr-1">
          {availablePolicies.length === 0 ? (
            <p className="text-muted-foreground text-sm">No policies available</p>
          ) : (
            <TooltipProvider>
              <div className="space-y-3">
                {availablePolicies.map(policy => {
                  const hasBeenSent = puiHistory.some(
                    pui => pui.productName === policy.productName && pui.isJoint === policy.isJoint,
                  );

                  const carrier = Object.values(insuranceCarrierDefs).find(carrierDef =>
                    carrierDef.policyTypes.includes(policy.fundingPolicyType),
                  );

                  const carrierLogo = carrier?.image;
                  const carrierName = carrier?.name;

                  const policyDisplayName = policy.productName;

                  const jointPolicyNote = policy.isJoint
                    ? 'Includes primary client and partner data'
                    : null;

                  const checkboxContent = (
                    <Label
                      htmlFor={policy.uniqueKey}
                      className={`flex w-full items-center gap-4 rounded-lg border border-gray-200 p-3 transition-colors hover:bg-gray-50 ${!policy.supportsPUI ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                    >
                      <Checkbox
                        id={policy.uniqueKey}
                        checked={selectedPolicies.has(policy.uniqueKey)}
                        onCheckedChange={() => handleTogglePolicy(policy.uniqueKey)}
                        disabled={!policy.supportsPUI}
                        className="shrink-0"
                      />

                      <div className="flex h-10 w-28 shrink-0 items-center justify-start">
                        {carrierLogo && (
                          <img
                            src={carrierLogo.src}
                            alt={carrierName || policy.productName}
                            className="max-h-8 max-w-[110px] object-contain"
                          />
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="block text-sm font-semibold">{policyDisplayName}</div>

                        {jointPolicyNote && (
                          <p className=" text-xs text-gray-500">{jointPolicyNote}</p>
                        )}

                        {hasBeenSent && (
                          <p className="text-xs font-medium text-yellow-600">Send Again?</p>
                        )}
                      </div>

                      <div className="shrink-0">
                        <UnderwritingStatus
                          productName={policy.productName}
                          fundingPolicyType={policy.fundingPolicyType}
                          size="sm"
                        />
                      </div>
                    </Label>
                  );

                  if (!policy.supportsPUI) {
                    return (
                      <Tooltip key={policy.uniqueKey}>
                        <TooltipTrigger asChild>
                          <div>{checkboxContent}</div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">This carrier does not support anonymized PUIs</p>
                        </TooltipContent>
                      </Tooltip>
                    );
                  }

                  return <div key={policy.uniqueKey}>{checkboxContent}</div>;
                })}
              </div>
            </TooltipProvider>
          )}
        </div>

        <div className="mt-6 flex justify-between">
          <Button
            onClick={onClose}
            variant="outline"
          >
            Cancel
          </Button>
          <Button onClick={handleContinue}>Draft Anonymized PUI</Button>
        </div>
      </>
    );
  };

  if (step === 'review-draft' && !showLoadingSequence && draftData?.intakeSections) {
    const selectedProducts = availablePolicies
      .filter(policy => selectedPolicies.has(policy.uniqueKey))
      .map(policy => (policy.isJoint ? `${policy.productName} (Joint)` : policy.productName));

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-8">
        <div className="w-full max-w-[90vw] overflow-hidden rounded-lg bg-white p-6 shadow-2xl">
          <PuiEmailReview
            intakeSections={draftData.intakeSections}
            partnerIntakeSections={draftData.partnerIntakeSections}
            products={selectedProducts}
            onSend={editedData => {
              handleSend(editedData);
            }}
            backToProducts={handleBackToSelection}
            onClose={onClose}
            isPending={isPending}
          />
        </div>
      </div>
    );
  }

  // Show confirmation screen in dialog
  if (step === 'show-confirmation' && emailResults) {
    return (
      <Dialog
        open={open}
        onOpenChange={onClose}
      >
        <DialogContent className="max-w-lg">
          <PuiSendConfirmation
            emailResults={emailResults}
            onComplete={onClose}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
    >
      <DialogContent>
        {showLoadingSequence ? (
          <PuiLoadingSequence
            isDataLoaded={draftData?.isLoading === false && draftData?.intakeSections !== undefined}
            onComplete={handleLoadingComplete}
          />
        ) : step === 'select-policies' ? (
          selectPoliciesContent()
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
